import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { useLocalizeMessage, Badge, Dropdown, Link, useDateTimeFormat, } from 'libs.nucleus';
import { isUnmodifiedPrimaryClick, shouldInterceptDocumentOpen } from '../../utils/signed_document_link.helpers';
import { statusLabelMap, statusMap } from '../../utils/signed_document_status.helpers';
export const useParticipantDocumentsTableColumns = ({ getDocumentHref, openDocumentsInModal, onOpenDocument, isPtsInteractionEnabled = false, onOpenPtsDocument, canOpenDocument = true, requiresIcfAccessWarning = false, canVoidDocument, onVoidDocument, onOpenDocumentDetails, getDocumentDetailsHref, }) => {
    const translate = useLocalizeMessage();
    const { formatDateTime } = useDateTimeFormat();
    const participantDocumentsColumns = useMemo(() => {
        return [
            {
                header: translate('generic.title'),
                accessorKey: 'ec__document_template.ec__title',
                cell: (context) => {
                    const title = context.getValue();
                    const document = context.row.original;
                    const documentHref = getDocumentHref(document);
                    const showPtsBadge = isPtsInteractionEnabled && !!document.ec__pts;
                    const interceptAllClicks = requiresIcfAccessWarning && openDocumentsInModal;
                    const renderTitleLink = () => {
                        if (!canOpenDocument) {
                            return _jsx("span", { children: title });
                        }
                        // The details page is the classic destination for a document title; the modal and
                        // the PTS viewer stay the fallback for callers without a details route.
                        if (onOpenDocumentDetails) {
                            return (_jsx(Link
                            // A real href so ⌘/ctrl-click, middle-click and Open in new tab work; the
                            // handler below still routes an ordinary click through the SPA.
                            , { 
                                // A real href so ⌘/ctrl-click, middle-click and Open in new tab work; the
                                // handler below still routes an ordinary click through the SPA.
                                href: getDocumentDetailsHref?.(document) ?? '#', "data-testid": 'participant-document-details-link', onClick: (event) => {
                                    if (!isUnmodifiedPrimaryClick(event)) {
                                        return;
                                    }
                                    event.preventDefault();
                                    onOpenDocumentDetails(document);
                                }, children: title }));
                        }
                        if (isPtsInteractionEnabled && document.ec__pts && onOpenPtsDocument) {
                            return (_jsx(Link, { href: interceptAllClicks ? '#' : documentHref || undefined, "data-testid": 'pts-document-link', onClick: (event) => {
                                    event.preventDefault();
                                    onOpenPtsDocument(document);
                                }, children: title }));
                        }
                        return (_jsx(Link, { href: interceptAllClicks ? '#' : documentHref || undefined, target: '_blank', rel: 'noreferrer', onClick: (event) => {
                                if (!openDocumentsInModal || !shouldInterceptDocumentOpen(event, interceptAllClicks)) {
                                    return;
                                }
                                event.preventDefault();
                                onOpenDocument(document);
                            }, children: title }));
                    };
                    return (_jsxs("div", { className: 'flex items-center gap-2', children: [renderTitleLink(), showPtsBadge && _jsx(Badge, { variant: 'common', label: translate('label.participant_documents.pts_badge') })] }));
                },
            },
            {
                header: translate('label.participant_documents.required_signers'),
                accessorKey: 'ec__required_signers',
            },
            {
                header: translate('generic.create', { tense: 'past' }),
                accessorKey: 'created',
                cell: (context) => formatDateTime(context.getValue()),
            },
            {
                header: translate('generic.status'),
                accessorKey: 'ec__status',
                cell: (context) => {
                    const status = context.getValue();
                    return _jsx(Badge, { variant: statusMap[status], label: translate(statusLabelMap[status]) });
                },
            },
            ...(canVoidDocument && onVoidDocument
                ? [
                    {
                        id: 'actions',
                        header: () => _jsx("span", { className: 'nucleus-sr-only', children: translate('generic.actions') }),
                        size: 48,
                        cell: (context) => {
                            const document = context.row.original;
                            if (!canVoidDocument(document)) {
                                return null;
                            }
                            const dropdownItems = [
                                {
                                    id: 'void',
                                    content: translate('action.participant_documents.void_consent'),
                                    onClick: () => onVoidDocument(document),
                                    destructive: true,
                                },
                            ];
                            return (_jsx("div", { className: 'flex justify-end', children: _jsx(Dropdown, { variant: 'kebab', items: dropdownItems }) }));
                        },
                    },
                ]
                : []),
        ];
    }, [
        canOpenDocument,
        canVoidDocument,
        formatDateTime,
        getDocumentDetailsHref,
        getDocumentHref,
        isPtsInteractionEnabled,
        onOpenDocument,
        onOpenDocumentDetails,
        onOpenPtsDocument,
        onVoidDocument,
        openDocumentsInModal,
        requiresIcfAccessWarning,
        translate,
    ]);
    return { participantDocumentsColumns };
};
